import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const pageTitleVariants = cva(
  "font-heading font-semibold text-foreground",
  {
    variants: {
      size: {
        default: "text-xl sm:text-2xl md:text-[28px] md:leading-9",
        lg: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl",
        xl: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl",
        sm: "text-lg sm:text-xl md:text-2xl",
      },
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      },
    },
    defaultVariants: {
      size: "default",
      align: "left",
    },
  }
);

const pageDescriptionVariants = cva(
  "text-muted-foreground",
  {
    variants: {
      size: {
        default: "text-xs sm:text-sm",
        lg: "text-sm sm:text-base md:text-lg",
        xl: "text-sm sm:text-base md:text-lg lg:text-xl",
        sm: "text-xs sm:text-sm",
      },
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      },
    },
    defaultVariants: {
      size: "default",
      align: "left",
    },
  }
);

export interface PageHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pageTitleVariants> {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}

const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, title, description, icon, actions, size, align, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-2",
          align === "center" && "items-center",
          align === "right" && "items-end",
          className
        )}
        {...props}
      >
        <div className={cn(
          "flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-3",
          align === "center" && "justify-center",
          align === "right" && "justify-end"
        )}>
          {icon && (
            <div className="flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <h1 className={cn(pageTitleVariants({ size, align }), "truncate")}>
              {title}
            </h1>
            {description && (
              <p className={cn(pageDescriptionVariants({ size, align }), "line-clamp-2")}>
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex w-full flex-shrink-0 sm:ml-auto sm:w-auto">
              {actions}
            </div>
          )}
        </div>
      </div>
    );
  }
);
PageHeader.displayName = "PageHeader";

// Standalone title component for simpler use cases
const PageTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & VariantProps<typeof pageTitleVariants>
>(({ className, size, align, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn(pageTitleVariants({ size, align }), className)}
    {...props}
  />
));
PageTitle.displayName = "PageTitle";

// Standalone description component
const PageDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & VariantProps<typeof pageDescriptionVariants>
>(({ className, size, align, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(pageDescriptionVariants({ size, align }), className)}
    {...props}
  />
));
PageDescription.displayName = "PageDescription";

export { PageHeader, PageTitle, PageDescription, pageTitleVariants, pageDescriptionVariants };
